import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActionsPanel = ({ upcomingBookings }) => {
  const [showWallet, setShowWallet] = useState(false);

  const handleDownloadTickets = () => {
    // Mock download functionality
    const tickets = upcomingBookings?.filter(booking => booking?.status === 'confirmed');
    if (tickets?.length > 0) {
      // Simulate download
      alert(`Downloading ${tickets?.length} ticket${tickets?.length > 1 ? 's' : ''} to your device...`);
    }
  };

  const handleAddToCalendar = () => {
    // Mock calendar integration
    const upcomingEvents = upcomingBookings?.filter(booking => 
      new Date(booking.eventDate) > new Date()
    );
    if (upcomingEvents?.length > 0) {
      alert(`Adding ${upcomingEvents?.length} event${upcomingEvents?.length > 1 ? 's' : ''} to your calendar...`);
    }
  };

  const handleShareEvent = (eventId) => {
    // Mock social sharing
    const shareUrl = `${window.location?.origin}/event-details?id=${eventId}`;
    navigator.clipboard?.writeText(shareUrl)?.then(() => {
      alert('Event link copied to clipboard!');
    });
  };

  const quickActions = [
    {
      id: 'download',
      label: 'Download Tickets',
      icon: 'Download',
      description: 'Get all your confirmed tickets',
      action: handleDownloadTickets,
      disabled: upcomingBookings?.filter(b => b?.status === 'confirmed')?.length === 0
    },
    {
      id: 'calendar',
      label: 'Add to Calendar',
      icon: 'CalendarPlus',
      description: 'Sync with your calendar app',
      action: handleAddToCalendar,
      disabled: upcomingBookings?.length === 0
    },
    {
      id: 'wallet',
      label: 'Digital Wallet',
      icon: 'Wallet',
      description: 'Access your mobile tickets',
      action: () => setShowWallet(true),
      disabled: false
    },
    {
      id: 'discover',
      label: 'Discover Events',
      icon: 'Search',
      description: 'Find new events to attend',
      action: null,
      link: '/event-discovery',
      disabled: false
    }
  ];

  return (
    <>
      <div className="bg-card border border-border rounded-lg p-4">
        <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {quickActions?.map((action) => (
            <div key={action?.id}>
              {action?.link ? (
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto p-4"
                  disabled={action?.disabled}
                  asChild
                >
                  <Link to={action?.link}>
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Icon name={action?.icon} size={20} className="text-primary" />
                      </div>
                      <div className="text-left">
                        <div className="font-medium text-foreground">{action?.label}</div>
                        <div className="text-sm text-muted-foreground">{action?.description}</div>
                      </div>
                    </div>
                  </Link>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto p-4"
                  onClick={action?.action}
                  disabled={action?.disabled}
                >
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Icon name={action?.icon} size={20} className="text-primary" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-foreground">{action?.label}</div>
                      <div className="text-sm text-muted-foreground">{action?.description}</div>
                    </div>
                  </div>
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Recent Activity */}
        <div className="mt-6 pt-4 border-t border-border">
          <h3 className="text-sm font-medium text-foreground mb-3">Recent Activity</h3>
          <div className="space-y-2">
            <div className="flex items-center space-x-3 text-sm">
              <div className="w-2 h-2 bg-success rounded-full"></div>
              <span className="text-muted-foreground">Checked in to Tech Conference 2025</span>
              <span className="text-xs text-muted-foreground ml-auto">2 hours ago</span>
            </div>
            <div className="flex items-center space-x-3 text-sm">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <span className="text-muted-foreground">Booked tickets for Jazz Night</span>
              <span className="text-xs text-muted-foreground ml-auto">1 day ago</span>
            </div>
            <div className="flex items-center space-x-3 text-sm">
              <div className="w-2 h-2 bg-warning rounded-full"></div>
              <span className="text-muted-foreground">Downloaded tickets for Art Exhibition</span>
              <span className="text-xs text-muted-foreground ml-auto">3 days ago</span>
            </div>
          </div>
        </div>
      </div>
      {/* Digital Wallet Modal */}
      {showWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Digital Wallet</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowWallet(false)}
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="space-y-3">
                {upcomingBookings?.filter(booking => booking?.status === 'confirmed')?.map((booking) => (
                    <div key={booking?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon name="Ticket" size={16} className="text-primary" />
                        </div>
                        <div>
                          <div className="font-medium text-foreground text-sm">{booking?.eventTitle}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(booking.eventDate)?.toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleShareEvent(booking?.eventId)}
                      >
                        <Icon name="Share" size={14} />
                      </Button>
                    </div>
                  ))}
              </div>
              
              {upcomingBookings?.filter(b => b?.status === 'confirmed')?.length === 0 && (
                <div className="text-center py-8">
                  <Icon name="Wallet" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No tickets in your wallet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickActionsPanel;